import { getActivityCounters as getActivityCountersRequest } from '@wix/ambassador-members-v1-activity-counter/http';
import type { ActivityCounter } from '@wix/ambassador-members-v1-activity-counter/types';
import type {
  IPlatformServices,
  ViewerScriptFlowAPI,
} from '@wix/yoshi-flow-editor';

import { NOTIFICATIONS_APP_ID } from '../../constants';
import { getNumberOfUnseenNotificationsActivityCounter } from '../../services/ping-feed-service';
import type { WixCodeApi } from '../../types';

interface Request {
  memberId: string;
  loggedIn: boolean;
  biService: IPlatformServices['bi'];
  flowAPI: ViewerScriptFlowAPI;
  wixCodeApi: WixCodeApi;
}

// Workaround for https://wix.atlassian.net/browse/MA-3846. We receive
// total notificationsCount from getActivityCounters and in our menus we
// want to show only unseen notificationsCount, which has the same
// counter key.
function filterActivityCounters(activityCounters: ActivityCounter[]) {
  const filteredCounters: ActivityCounter[] = [];

  activityCounters.forEach((activityCounter) => {
    if (activityCounter.appId !== NOTIFICATIONS_APP_ID) {
      return filteredCounters.push(activityCounter);
    }

    const counters = activityCounter.counters?.filter(
      ({ key }) => key !== 'notificationsCount',
    );

    if (counters?.length) {
      filteredCounters.push({
        ...activityCounter,
        counters,
      });
    }
  });

  return filteredCounters;
}

async function getActivityCounters(
  flowAPI: ViewerScriptFlowAPI,
  memberId: string,
) {
  const {
    httpClient,
    errorHandler: { withErrorHandler },
  } = flowAPI;

  const requestFn = () =>
    httpClient.request(getActivityCountersRequest({ memberId }));

  return withErrorHandler(requestFn, {
    errorCodesMap: {},
  });
}

export async function getMemberActivityCounters({
  memberId,
  loggedIn,
  biService,
  flowAPI,
  wixCodeApi,
}: Request) {
  try {
    const [activityCountersResponse, numberOfUnseenActivityCounter] =
      await Promise.all([
        getActivityCounters(flowAPI, memberId),
        loggedIn
          ? getNumberOfUnseenNotificationsActivityCounter({
              metaSiteId: biService?.bi?.metaSiteId,
              memberId,
              flowAPI,
              wixCodeApi,
            })
          : null,
      ]);

    const activityCounters = filterActivityCounters(
      activityCountersResponse?.data?.activityCounters || [],
    );

    if (numberOfUnseenActivityCounter) {
      activityCounters.push(numberOfUnseenActivityCounter);
    }

    const apps = activityCounters.map((activityCounter) => ({
      appDefId: activityCounter.appId,
      numbers: activityCounter.counters?.reduce(
        (
          acc: Record<string, { public?: boolean; count?: number }>,
          counter,
        ) => {
          acc[counter.key!] = {
            public: counter.public,
            count: counter.count,
          };
          return acc;
        },
        {},
      ),
    }));

    return apps;
  } catch (error) {
    const { httpError } = flowAPI.errorHandler.getResolvedError(error);
    flowAPI.panoramaClient
      ?.errorMonitor()
      .reportError((httpError || error) as Error);

    return [];
  }
}

export type MemberActivityCounters = Awaited<
  ReturnType<typeof getMemberActivityCounters>
>;
